import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ChevronLeft,
  Clock,
  CheckCircle,
  Loader,
  AlertCircle,
  Info,
  Sun,
  Sunset,
  Moon,
} from 'lucide-react';
import {
  DELIVERY_SLOT_GROUP_DEFINITIONS,
  deliverySlotsByIds,
  findDeliverySlot,
} from '../../utils/deliverySlots';
import { fetchLimaDistrictCatalog, isAfternoonOnlyDistrict } from '../../utils/locationCatalog';

const API_URL = process.env.REACT_APP_API_URL;

const SLOT_GROUP_VISUALS = {
  'early-afternoon': {
    icon: Sun,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    selectedBg: 'from-orange-400/15 to-orange-200/10',
    selectedBorder: 'border-orange-400',
    iconBg: 'bg-orange-400',
  },
  'late-afternoon': {
    icon: Sunset,
    color: 'text-brand-primary',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    selectedBg: 'from-brand-primary/15 to-brand-gold/10',
    selectedBorder: 'border-brand-primary',
    iconBg: 'bg-brand-primary',
  },
  evening: {
    icon: Moon,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    selectedBg: 'from-indigo-400/15 to-indigo-200/10',
    selectedBorder: 'border-indigo-400',
    iconBg: 'bg-indigo-500',
  },
};

// All time slot groups use the shared slot catalog for labels/windows.
const SLOT_GROUPS = DELIVERY_SLOT_GROUP_DEFINITIONS.map((group) => ({
  ...group,
  ...(SLOT_GROUP_VISUALS[group.id] || {}),
  slots: deliverySlotsByIds(group.slotIds),
}));

function TimeSlotSelector({ delivery, onBack }) {
  const [selectedSlot, setSelectedSlot] = useState(delivery.slot || null);
  const [districtCatalog, setDistrictCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchLimaDistrictCatalog()
      .then(setDistrictCatalog)
      .catch(() => setDistrictCatalog([]));
  }, []);

  const isAfternoonOnly = isAfternoonOnlyDistrict(delivery.district, districtCatalog);

  // Filter groups based on district
  const visibleGroups = isAfternoonOnly
    ? SLOT_GROUPS.filter((g) => g.afternoonOnly)
    : SLOT_GROUPS;

  const handleSelectSlot = (slotId) => {
    setSelectedSlot(slotId);
    setError(null);
    setSuccess(false);
  };

  const handleSaveSlot = async () => {
    if (!selectedSlot) {
      setError('Por favor, selecciona un horario');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await axios.post(`${API_URL}/api/delivery_points/${delivery.id}/slot`, {
        slot: selectedSlot,
      });
      setSuccess(true);
      setTimeout(() => {
        onBack();
      }, 2000);
    } catch (err) {
      console.error('Error saving slot:', err);
      setError('No se pudo guardar el horario. Por favor, intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const selectedSlotLabel = findDeliverySlot(selectedSlot)?.label;
  const currentSlotLabel = findDeliverySlot(delivery.slot)?.label;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-green to-brand-primary shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              disabled={saving}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200 active:scale-95"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-lg shadow-md">
                <Clock className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat">
                  Horario de Entrega
                </h1>
                <p className="text-xs text-white/90 font-montserrat">
                  Elige tu ventana de 3 horas
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-5">

          {/* Afternoon-only district notice */}
          {isAfternoonOnly && (
            <div className="bg-gradient-to-r from-brand-gold/10 to-brand-primary/10 border border-brand-gold/30 rounded-2xl p-5">
              <div className="flex gap-3">
                <Info className="w-6 h-6 text-brand-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-brand-primary font-montserrat mb-1">
                    Horarios para {delivery.district}
                  </h3>
                  <p className="text-sm text-slate-700 font-montserrat">
                    Para tu distrito, ofrecemos entregas en horario de tarde (4:00 PM - 9:00 PM)
                    para garantizar una mejor logística y servicio.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* General instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
            <div className="flex gap-3">
              <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-blue-900 font-montserrat mb-2">
                  Instrucciones Importantes
                </h3>
                <ul className="space-y-2 text-sm text-blue-800 font-montserrat">
                  <li className="flex gap-2">
                    <span className="font-bold">•</span>
                    <span>Selecciona una ventana de <strong>3 horas</strong> que te sea conveniente</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">•</span>
                    <span>
                      {isAfternoonOnly
                        ? 'Para tu distrito, los horarios disponibles son de 4:00 PM a 9:00 PM'
                        : 'Nuestros horarios de entrega son de 1:00 PM a 9:00 PM'}
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">•</span>
                    <span>El horario puede variar ligeramente debido al tráfico u otros factores</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">•</span>
                    <span>Te avisaremos cuando el conductor esté cerca</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Current slot reminder */}
          {delivery.slot && !success && (
            <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-2xl p-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-700 font-montserrat">Horario actual seleccionado:</p>
                  <p className="font-bold text-brand-primary font-montserrat">{currentSlotLabel || 'No definido'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
              <div className="flex gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-green-900 font-montserrat mb-1">
                    ¡Horario guardado exitosamente!
                  </h3>
                  <p className="text-sm text-green-800 font-montserrat">
                    Tu ventana de entrega ha sido actualizada. Regresando al menú principal...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
              <div className="flex gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 font-montserrat">{error}</p>
              </div>
            </div>
          )}

          {/* Slot Groups */}
          <div className="space-y-6">
            {visibleGroups.map((group) => {
              const GroupIcon = group.icon;
              return (
                <div key={group.id}>
                  {/* Group header */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-7 h-7 rounded-lg ${group.iconBg} flex items-center justify-center`}>
                      <GroupIcon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className={`text-sm font-bold font-montserrat uppercase tracking-wide ${group.color}`}>
                      {group.label}
                    </h3>
                  </div>

                  {/* Slots */}
                  <div className="space-y-2.5">
                    {group.slots.map((slot) => {
                      const isSelected = selectedSlot === slot.id;
                      const isCurrentSlot = delivery.slot === slot.id;

                      return (
                        <button
                          key={slot.id}
                          onClick={() => handleSelectSlot(slot.id)}
                          disabled={saving}
                          className={`w-full rounded-2xl border-2 p-4 transition-all duration-200 text-left
                            ${isSelected
                              ? `border-2 ${group.selectedBorder} bg-gradient-to-r ${group.selectedBg} shadow-md scale-[1.015]`
                              : isCurrentSlot
                              ? `${group.borderColor} ${group.bgColor}`
                              : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                            }
                            ${saving ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                isSelected
                                  ? group.iconBg
                                  : isCurrentSlot
                                  ? group.iconBg + '/70'
                                  : 'bg-slate-100'
                              }`}>
                                <Clock className={`w-5 h-5 ${
                                  isSelected || isCurrentSlot ? 'text-white' : 'text-slate-400'
                                }`} />
                              </div>
                              <div>
                                <p className={`text-base font-bold font-montserrat ${
                                  isSelected ? group.color : 'text-slate-800'
                                }`}>
                                  {slot.label}
                                </p>
                                <p className="text-xs text-slate-500 font-montserrat">Ventana de 3 horas</p>
                                {isCurrentSlot && !isSelected && (
                                  <p className={`text-xs font-semibold font-montserrat mt-0.5 ${group.color}`}>
                                    Horario actual
                                  </p>
                                )}
                              </div>
                            </div>

                            {isSelected && (
                              <div className={`w-7 h-7 rounded-full ${group.iconBg} flex items-center justify-center flex-shrink-0`}>
                                <CheckCircle className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer note */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600 font-montserrat">
                <strong>Nota:</strong> La hora exacta puede variar dentro de la ventana seleccionada debido a tráfico,
                clima y otras entregas en la ruta. Te mantendremos informado sobre el progreso de tu entrega.
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Sticky action button */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 px-4 py-4 safe-area-bottom">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={handleSaveSlot}
            disabled={!selectedSlot || saving || success}
            className={`w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-bold font-montserrat text-lg transition-all
              ${selectedSlot && !saving && !success
                ? 'bg-gradient-to-r from-brand-primary to-brand-gold text-white hover:shadow-lg active:scale-95'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
          >
            {saving ? (
              <>
                <Loader className="w-6 h-6 animate-spin" />
                <span>Guardando...</span>
              </>
            ) : success ? (
              <>
                <CheckCircle className="w-6 h-6" />
                <span>Guardado</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-6 h-6" />
                <span>Confirmar Horario</span>
              </>
            )}
          </button>

          {selectedSlot && !success && (
            <p className="text-center text-xs text-slate-600 font-montserrat mt-3">
              Has seleccionado: <strong>{selectedSlotLabel}</strong>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default TimeSlotSelector;
